// frontend/src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, visibleDepartmentsFor, canViewSalesTeam, canViewAeDashboards } from '../App';
import { useTheme } from '../theme/ThemeContext';
import { logout } from '../api/client';

// Tracks a CSS media query. Used to swap the desktop sidebar for the mobile
// top bar + drawer at 768px.
function useMediaQuery(queryStr) {
  const [matches, setMatches] = useState(() => window.matchMedia(queryStr).matches);
  useEffect(() => {
    const mq = window.matchMedia(queryStr);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [queryStr]);
  return matches;
}

export default function Layout() {
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever we leave mobile so it can't linger open
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: '100vh', background: 'var(--bg-base)' }}>
      {isMobile ? (
        <>
          {/* Mobile top bar */}
          <header className="mobile-topbar">
            <img src="/weaver-logo-white.png" alt="Weaver" />
            <button
              className="hamburger-btn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          </header>

          {/* Drawer + backdrop */}
          {drawerOpen && (
            <>
              <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
              <Sidebar
                user={user}
                collapsed={false}
                isDrawer
                onNavigate={() => setDrawerOpen(false)}
              />
            </>
          )}
        </>
      ) : (
        <Sidebar
          user={user}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(c => !c)}
        />
      )}

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <Outlet />
      </main>
    </div>
  );
}

function Sidebar({ user, collapsed, isDrawer = false, onNavigate, onToggleCollapse }) {
  const { theme, toggleTheme } = useTheme();

  return (
    <aside
      className={`sidebar${isDrawer ? ' drawer' : ''}`}
      style={isDrawer ? undefined : { width: collapsed ? '64px' : '230px' }}
    >
      {/* Logo + app label */}
      <div className="sidebar-header" style={collapsed ? { padding: '18px 10px' } : undefined}>
        {!collapsed && (
          <>
            <img
              src="/weaver-logo-white.png"
              alt="Weaver — Right IT Solution. Right Time. Right Reason."
              className="sidebar-logo"
            />
            <div className="sidebar-applabel">Commissions</div>
          </>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, paddingBottom: '16px', display: 'flex', flexDirection: 'column' }}>
        {collapsed && <div style={{ height: '16px' }} />}
        <NavItems user={user} collapsed={collapsed} onNavigate={onNavigate} />
      </nav>

      {/* User + controls */}
      <div className="sidebar-footer">
        {!collapsed && (
          <div style={{ marginBottom: '12px' }}>
            <div className="sidebar-user-name">{user?.display_name}</div>
            <div className="sidebar-user-role">{DEPT_LABELS[user?.department] || 'Sales'} · {user?.department === 'presales' && user?.app_role === 'rep' ? 'PSE' : user?.app_role?.replace('_', ' ')}</div>
          </div>
        )}

        {/* Theme toggle (main content only — sidebar stays brand navy) */}
        <button
          onClick={toggleTheme}
          className="sidebar-btn"
          style={{ width: '100%', marginBottom: '8px', padding: collapsed ? '7px 0' : undefined }}
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {theme === 'light' ? '☾' : '☀'}
          {!collapsed && <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>}
        </button>

        <div style={{ display: 'flex', gap: '8px', justifyContent: collapsed ? 'center' : 'space-between' }}>
          {!isDrawer && (
            <button
              onClick={onToggleCollapse}
              className="sidebar-btn"
              style={{ padding: '7px 11px' }}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? '→' : '←'}
            </button>
          )}
          {!collapsed && (
            <button onClick={logout} className="sidebar-btn" style={isDrawer ? { flex: 1 } : undefined}>
              Sign out
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

const DEPT_LABELS = { sales: 'Sales', services: 'Services', operations: 'Operations', presales: 'Pre-Sales', finance: 'Finance' };
const DEPT_ICONS  = { sales: '◈', services: '⚒', operations: '▣', presales: '◇', finance: '◎' };
// Remembered per browser AND per user, so one person's last department never
// leaks into the next person's session on a shared machine.
const viewDeptKey = (user) => `commissions.viewDept.${user?.id ?? 'anon'}`;

// The sidebar is organised by DEPARTMENT. A user sees their own department's
// menu; members of Operations / Finance (and global admins) get a department
// switcher at the top and can page through every department. Role-gated pages
// (Team View, the Finance panel) still appear only for the roles that may use
// them — department decides what is listed, role decides what is allowed.
function NavItems({ user, collapsed, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const role          = user?.app_role;
  const isFinance     = ['finance_admin', 'global_admin'].includes(role);
  const ownDept       = user?.department || 'sales';
  const visible       = visibleDepartmentsFor(user);
  const crossDept     = visible.length > 1;

  // Which department the menu is showing. Remembered per browser so a Finance
  // user who was looking at Services comes back to Services.
  const [viewDept, setViewDept] = useState(() => {
    let saved = null;
    try { saved = localStorage.getItem(viewDeptKey(user)); } catch { /* storage unavailable */ }
    if (saved && visible.includes(saved)) return saved;
    return visible.includes(ownDept) ? ownDept : visible[0];
  });
  useEffect(() => {
    try { localStorage.setItem(viewDeptKey(user), viewDept); } catch { /* storage unavailable */ }
  }, [viewDept, user]);

  // Follow the URL: opening /departments/services (a link, a refresh) moves the
  // switcher to Services; Team View always belongs to Sales.
  useEffect(() => {
    const m = location.pathname.match(/^\/departments\/([a-z]+)/);
    const fromUrl = m ? m[1] : (location.pathname.startsWith('/team') || location.pathname.startsWith('/sales/')) ? 'sales' : location.pathname.startsWith('/presales/') ? 'presales' : null;
    if (fromUrl && visible.includes(fromUrl) && fromUrl !== viewDept) setViewDept(fromUrl);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where "open this department" lands.
  const homeFor = (code) => {
    if (code === 'sales') {
      if (ownDept === 'sales') return '/dashboard';
      return canViewSalesTeam(user) ? '/team' : '/departments/sales';
    }
    return `/departments/${code}`;
  };
  const switchDept = (code) => {
    if (!visible.includes(code)) return;
    setViewDept(code);
    navigate(homeFor(code));
    onNavigate?.();
  };

  // The menu for the department being viewed.
  const items = [];
  if (viewDept === 'sales') {
    const salesViewer = canViewSalesTeam(user);           // role, or cross-department viewer
    if (ownDept === 'sales')                  items.push({ to: '/dashboard', icon: '◈', label: 'My Dashboard' });
    if (salesViewer)                          items.push({ to: '/team',      icon: '◉', label: 'Team View' });
    // Manager comp (paid on bookings). A Sales manager already has it as My
    // Dashboard; everyone else who may see the Sales team gets it here.
    if (salesViewer && !(role === 'manager' && ownDept === 'sales')) {
      items.push({ to: '/sales/manager-comp', icon: '◆', label: 'Manager Comp' });
    }
    // Company overview (closed-won to goal, shipped GP, pending): finance roles
    // outside Sales reach it here; inside Sales it is their My Dashboard.
    if (isFinance && ownDept !== 'sales')      items.push({ to: '/sales/overview', icon: '◎', label: 'Company Overview' });
    // Individual AE dashboards: global admins viewing Sales from Operations / Finance.
    if (canViewAeDashboards(user))            items.push({ to: '/sales/ae', icon: '◍', label: 'AE Dashboards' });
    if (salesViewer)                          items.push({ to: '/departments/sales', icon: '▤', label: 'Sales Overview' });
  } else {
    // Non-sales lens: the department dashboard only. Sales pages (company overview,
    // View as, Team View …) live under the Sales lens.
    // A Pre-Sales member's own-plan page is the "PSE View" (their equivalent of My Dashboard).
    const pseMember = viewDept === 'presales' && ownDept === 'presales' && !isFinance && !crossDept;
    items.push({ to: `/departments/${viewDept}`, icon: pseMember ? '◈' : (DEPT_ICONS[viewDept] || '▤'), label: pseMember ? 'PSE View' : `${DEPT_LABELS[viewDept]} Dashboard` });
    // Viewers of Pre-Sales also get the PSE View (the Senior Solutions Architect plan).
    if (viewDept === 'presales' && !pseMember) items.push({ to: '/presales/pse', icon: '◈', label: 'PSE View' });
  }

  return (
    <>
      {!collapsed && (
        <>
          <div className="sidebar-section-label">Department</div>
          {crossDept ? (
            <select
              className="sidebar-select"
              value={viewDept}
              onChange={e => switchDept(e.target.value)}
              aria-label="Switch department"
              title="Operations and Finance can view every department"
            >
              {visible.map(code => <option key={code} value={code}>{DEPT_LABELS[code] || code}</option>)}
            </select>
          ) : (
            <div className="sidebar-dept-static">{DEPT_ICONS[viewDept]} {DEPT_LABELS[viewDept] || viewDept}</div>
          )}
          <div className="sidebar-section-label">Menu</div>
        </>
      )}
      {items.map(it => (
        <NavItem key={it.to} to={it.to} icon={it.icon} label={it.label} collapsed={collapsed} onNavigate={onNavigate} />
      ))}
      {isFinance && (
        <>
          {!collapsed && <div className="sidebar-section-label">Administration</div>}
          {collapsed && <div style={{ height: '12px' }} />}
          <NavItem to="/finance" icon="◎" label="Finance" collapsed={collapsed} onNavigate={onNavigate} />
        </>
      )}
    </>
  );
}

function NavItem({ to, icon, label, collapsed, onNavigate }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
      style={collapsed ? { justifyContent: 'center', padding: '10px 0' } : undefined}
      title={collapsed ? label : undefined}
    >
      <span style={{ fontSize: '16px', flexShrink: 0 }}>{icon}</span>
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}
